import React, {
  ReactNode,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  ReactElement,
  isValidElement,
} from "react";
import Image from "next/image";
import clsx from "clsx";

import {
  MessageCircle,
  RefreshCw,
  Share2,
  MapPin,
  ImageIcon,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Eye,
  LoaderCircle,
  CircleX,
  CircleUser,
  Sparkles,
} from "lucide-react";
import { useMutation, useReactiveVar } from "@apollo/client";
import { ADD_IMPRESSIONS } from "@/api/apollo-client/mutations/mutations";
import LinkPreview from "../LinkPreview";
import AutomatedPostLinkPreview from "../AutomatedPostLinkPreview";
import { FormattedPost, Post, Media } from "@/types";
import { parsePostText } from "@/utils/parsePostText";
import { Spinner } from "@/components/ui/spinner";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import countReduce from "@/utils/countReduce";
import Link from "next/link";
import { formatTimestamp } from "@/utils/formatTime";
import { useRouter } from "next/navigation";
import { PostType } from "@/constants";
import { YouTubeEmbed } from "@/components/Posts/YoutubeEmbed";
import { extractYouTubeVideoId } from "@/utils/youtubeId";

import PostMenu from "./PostMenu";
import { useAuth } from "@/contexts/authContext";
import { sendClickEvent } from "../Analytics";
import defaultProfile from "@/images/default-profile.jpg";
import ReactionPopup from "../ReactionIcon";
import ReactionsPanel from "@/components/reactions/ReactionsPanel";
import VideoPlayer from "./VideoPlayer";
import AdComponent from "../AdSense";
import SharePanel from "@/components/share/SharePanel";
import { plainPostText } from "@/utils/plainPostText";
import { useSharePost } from "@/hooks/useSharePost";
import { HoverableMention } from "../HoverableMention";
import { buttonTooltipDismissedVar } from "../HomePage";
import AiAnalysis from "./AiAnalysisBlock";
import {
  DISPLAY_NAME_MAX_LENGTH_POST_DISPLAY,
  HANDLE_MAX_WIDTH_POST_DISPLAY,
} from "@/utils/uiConstant";

interface PostDisplayProps {
  scrollRef?: React.RefObject<HTMLDivElement>;
  posts: FormattedPost[];
  userPosts?: FormattedPost[];
  onLoadMore?: () => void;
  loading?: boolean;
  hasMore?: boolean;
  context?: "homepage" | "profile";
  onMediaError?: (postId: number, mediaIndex: number) => void;
  mediaErrors?: Record<string, boolean>;
}
// interface AssociatedPost {
//   id: string;
//   type: string;
//   content: string;
//   associated_with: Mention[];
//   mentioned: Mention[];
// }

const PostDisplay: React.FC<PostDisplayProps> = ({
  posts,
  scrollRef,
  userPosts = [],
  onLoadMore,
  loading = true,
  hasMore = true,
  context = "homepage",
  onMediaError,
  mediaErrors = {},
}) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const failedMediaRef = useRef(new Set<string>());
  const [selectedMedia, setSelectedMedia] = useState<{
    media: Media[];
    currentIndex: number;
  } | null>(null);

  const [showPopup, setShowPopup] = useState(false);

  const [showAssociationDropdown, setShowAssociationDropdown] = useState(false);
  const [selectedAssociationPost, setSelectedAssociationPost] =
    useState<FormattedPost | null>(null);
  const [associationButton, setAssociationButton] =
    useState<HTMLButtonElement | null>(null);
  const associationDropdownRef = useRef<HTMLDivElement | null>(null);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // Reactions panel state
  const [isReactionsOpen, setIsReactionsOpen] = useState(false);
  const [reactionsPostId, setReactionsPostId] = useState<string | null>(null);
  const openReactionsPanel = (postId: string) => {
    setReactionsPostId(postId);
    setIsReactionsOpen(true);
  };
  const closeReactionsPanel = () => setIsReactionsOpen(false);

  const router = useRouter();
  const [expandedPosts, setExpandedPosts] = useState<{
    [postId: string]: boolean;
  }>({});
  const [overflowingPosts, setOverflowingPosts] = useState<{
    [postId: string]: boolean;
  }>({});
  const contentRefs = useRef<{ [postId: string]: HTMLParagraphElement | null }>(
    {}
  );

  const [showAssociationSheet, setShowAssociationSheet] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Track which post's AI panel is open
  const [aiOpenMap, setAiOpenMap] = useState<{ [postId: string]: boolean }>({});

  const { isAuthenticated } = useAuth();
  const [addImpression] = useMutation(ADD_IMPRESSIONS);

  // Share functionality
  const { isSharePanelOpen, sharePost, openSharePanel, closeSharePanel } =
    useSharePost();

  const MINIMUM_OBSERVE_TIME = 400;
  const POST_IMPRESSION_LIST_DEBOUNCE_TIME = 2000;

  const postVisibilityTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const mutationDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const POST_ACTION_BUTTON_SIZE = 20;

  const sendImpressions = () => {
    const observedPosts = JSON.parse(
      localStorage.getItem("observed_posts") || "[]"
    );
    if (observedPosts.length > 0) {
      addImpression({
        variables: { input: { impression_on: observedPosts } },
      });
      localStorage.setItem("observed_posts", "[]");
    }
  };

  const handlePostVisibilityChange = (postId: string, isVisible: boolean) => {
    if (isVisible) {
      if (!postVisibilityTimers.current[postId]) {
        postVisibilityTimers.current[postId] = setTimeout(() => {
          const observedPosts = JSON.parse(
            localStorage.getItem("observed_posts") || "[]"
          );
          if (!observedPosts.includes(postId)) {
            observedPosts.push(postId);
            localStorage.setItem(
              "observed_posts",
              JSON.stringify(observedPosts)
            );
          }
          // Reset debounce timer whenever a new post is added
          if (mutationDebounceTimer.current) {
            clearTimeout(mutationDebounceTimer.current);
          }
          mutationDebounceTimer.current = setTimeout(
            sendImpressions,
            POST_IMPRESSION_LIST_DEBOUNCE_TIME
          );
        }, MINIMUM_OBSERVE_TIME);
      }
    } else {
      if (postVisibilityTimers.current[postId]) {
        clearTimeout(postVisibilityTimers.current[postId]);
        delete postVisibilityTimers.current[postId];
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const postId = entry.target.getAttribute("data-post-id");
          if (postId) {
            handlePostVisibilityChange(postId, entry.isIntersecting);
          }
        });
      },
      { threshold: 0.5 }
    );

    const postElements = document.querySelectorAll("[data-post-id]");
    postElements.forEach((el) => observer.observe(el));

    const handleBeforeUnload = () => {
      if (mutationDebounceTimer.current) {
        clearTimeout(mutationDebounceTimer.current);
      }
      sendImpressions();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      observer.disconnect();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (mutationDebounceTimer.current) {
        clearTimeout(mutationDebounceTimer.current);
      }
    };
  }, [posts]);

  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openDropdownId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && onLoadMore) {
          onLoadMore();
        }
      },
      { rootMargin: "500px", threshold: 0.1 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [onLoadMore, hasMore, loading]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showAssociationDropdown) return;
    function handleClick(e: MouseEvent) {
      if (
        associationDropdownRef.current &&
        !associationDropdownRef.current.contains(e.target as Node) &&
        associationButton &&
        !associationButton.contains(e.target as Node)
      ) {
        setShowAssociationDropdown(false);
        setSelectedAssociationPost(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showAssociationDropdown, associationButton]);

  // In the effect for showing the dropdown, calculate position
  useLayoutEffect(() => {
    if (showAssociationDropdown && associationButton) {
      const btnRect = associationButton.getBoundingClientRect();
      const dropdownWidth = 240; // px, adjust if needed
      const scrollContainer = scrollRef?.current;
      const scrollX = scrollContainer?.scrollLeft ?? window.scrollX;
      const scrollY = scrollContainer?.scrollTop ?? window.scrollY;

      let left = btnRect.left + scrollX;
      let top = btnRect.bottom + scrollY; // align to bottom of button
      if (left + dropdownWidth > window.innerWidth) {
        // Not enough space on right, show to the left
        left = btnRect.right + scrollX - dropdownWidth;
      }
      setDropdownPosition({ top, left });
    }
    if (!showAssociationDropdown) {
      setDropdownPosition(null);
    }
  }, [showAssociationDropdown, associationButton, scrollRef]);

  const handleClick = (postId: string | number) => {
    const scrollY = scrollRef?.current?.scrollTop || 0;
    sessionStorage.setItem("scroll-pos", scrollY.toString());
    router.push(`/post/${postId}`);

    sendClickEvent({
      category: "Button",
      action: "post_click",
      label: "click a post",
      value: 1,
      extraParams: {
        postId: postId,
      },
    });
  };

  const handleCommentClick = (postId: string | number) => {
    handleClick(postId); // navigates to /post/id

    // Append #comments after a short delay
    setTimeout(() => {
      router.push(`/post/${postId}#comments`);
    }, 100); // Adjust delay as needed
  };

  const handleShare = (post: FormattedPost) => {
    if (isAuthenticated) {
      // For authenticated users, open the SharePanel
      openSharePanel({
        ...post,
        content: plainPostText(post?.content || ""),
      } as any);
    } else {
      // For unauthenticated users, use native share or copy link
      const isClip = Boolean(post?.media?.some((m) => m.type === "video"));
      const postUrl = `${window.location.origin}/${isClip ? "clips" : "post"}/${post?.id}`;

      if (navigator.share) {
        // Parse the post content and convert to plain text
        const plainText = plainPostText(post?.content || "");
        const truncatedText =
          plainText.length > 100
            ? plainText.substring(0, 100) + "..."
            : plainText;

        navigator
          .share({
            title: `${post?.username}'s ${isClip ? "clip" : "post"}`,
            text: truncatedText,
            url: postUrl,
          })
          .catch((error) => console.error("Error sharing:", error));
      } else {
        // Fallback: copy link to clipboard
        navigator.clipboard?.writeText(postUrl);
        alert("Link copied to clipboard!");
      }
    }
  };

  const handleMediaClick = (
    media: Media,
    mediaArray: Media[],
    index: number
  ) => {
    setSelectedMedia({ media: mediaArray, currentIndex: index });
  };
  const handleProfileClick = (userName: string) => {
    router.push(`/profile/${userName}`);
  };

  const handleMediaChange = (index: number) => {
    setSelectedMedia((prev) => {
      if (prev) {
        return { ...prev, currentIndex: index };
      }
      return prev;
    });
  };

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urlTestRegex = /^https?:\/\/[^\s]+$/;

  function linkifyReactNode(node: ReactNode): ReactNode {
    if (typeof node === "string") {
      const parts = node.split(urlRegex);
      return parts.map((part, i) =>
        urlTestRegex.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        ) : (
          part
        )
      );
    }

    if (Array.isArray(node)) {
      return node.map((child, i) => (
        <React.Fragment key={i}>{linkifyReactNode(child)}</React.Fragment>
      ));
    }

    if (isValidElement(node)) {
      // Skip processing if element is already a link (anchor tag or Link component)
      const element = node as ReactElement<any>;
      if (element.type === "a" || element.type === Link) {
        return node; // Return as-is to avoid nested anchors
      }

      // Narrow type to ReactElement with props having children
      return React.cloneElement(element, {
        children: linkifyReactNode(element.props.children),
      });
    }

    return node;
  }

  const RenderPostContent = ({ post }: { post: FormattedPost }) => {
    const parsedContent: ReactNode = parsePostText(
      post?.content,
      post?.mentioned?.map((m) => ({
        id: String(m.id),
        name: m.name,
        avatar: m.avatar,
        bio: m.profile_bio,
      })) || [],
      (post?.tags || []).map((t) => ({ tag_id: t.id, tag_name: t.name }))
    );

    const contentWithLinks = linkifyReactNode(parsedContent);

    return (
      <p className="whitespace-pre-wrap break-words max-w-full">
        {contentWithLinks}
      </p>
    );
  };

  const handleMediaError = (
    postId: number,
    mediaIndex: number,
    mediaUrl: string
  ) => {
    const errorKey = `${postId}-${mediaIndex}`;
    failedMediaRef.current.add(mediaUrl);
    if (onMediaError) {
      onMediaError(postId, mediaIndex);
    }
  };

  const isMediaFailed = (mediaUrl: string) => {
    return failedMediaRef.current.has(mediaUrl);
  };

  // Detect overflow for each post after render
  useEffect(() => {
    posts.forEach((post) => {
      const ref = contentRefs.current[post.id];
      if (ref) {
        const isOverflowing = ref.scrollHeight > ref.clientHeight + 2; // +2 for rounding
        setOverflowingPosts((prev) => ({ ...prev, [post.id]: isOverflowing }));
      }
    });
  }, [posts]);

  const handleLikeClick = (post: FormattedPost) => {
    if (!isAuthenticated) {
      router.push("/login");
      return;
    }
    sendClickEvent({
      category: "Button",
      action: "post_like",
      label: "Liked a post",
      value: 1,
      extraParams: {
        postId: post.id,
      },
    });
  };

  const renderPost = (
    post: FormattedPost,
    index: number,
    arr: FormattedPost[]
  ) => {
    const isUserPost = post?.isUserPost || false;
    const isAuthor = isUserPost;
    const formattedLocation = post?.location_name;
    const hasAssociation = Boolean(
      post.associated_with && post.associated_with.length > 0
    );
    const hasTypeTag = post.type.toLowerCase() !== "general";
    const showFullHeader = !(hasAssociation || hasTypeTag);
    const triggerIndex = Math.max(0, arr.length - 5);
    const postRef =
      index === triggerIndex
        ? (loaderRef as React.RefObject<HTMLDivElement>)
        : null;
    const watchDrag = (emblaApi: any, event: any) => {
      return !event.target.closest(".no-drag");
    };

    const user = {
      id: post.id,
      bio: post.author.profile_bio,
      name: post.author.name,
      display_name: post.author.display_name,
      avatar: post.author.profile_picture?.url,
    };

    const fullDisplayName = post?.author.display_name || post.author.name;
    const displayNameShort =
      fullDisplayName?.length > DISPLAY_NAME_MAX_LENGTH_POST_DISPLAY
        ? fullDisplayName.slice(0, DISPLAY_NAME_MAX_LENGTH_POST_DISPLAY)
        : fullDisplayName;
    const handleShort =
      post.username?.length > HANDLE_MAX_WIDTH_POST_DISPLAY
        ? post.username.slice(0, HANDLE_MAX_WIDTH_POST_DISPLAY)
        : post.username;

    const hasAiFields = post?.ai_analysis != undefined ? true : false;

    return (
      <>
        <div
          key={post?.id}
          ref={postRef}
          data-post-id={post.id}
          className={`bg-white z-10 dark:bg-gray-950 rounded-xl p-3 mt-3 sm:p-4 flex flex-col cursor-pointer transition-all duration-300 ease-in-out max-w-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-900 ${
            hasAiFields
              ? "border-2 border-cyan-500/30 dark:border-cyan-500/50 bg-gradient-to-br from-white to-cyan-50/30 dark:from-gray-950 dark:to-cyan-950/20 shadow-cyan-500/10 dark:shadow-cyan-500/20 hover:border-cyan-500/50 dark:hover:border-cyan-400/60"
              : "border border-gray-200 dark:border-gray-800"
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClick(post?.id);
          }}
        >
          {/* upper portion */}
          <div className="flex flex-row w-full mb-2">
            <div className="flex items-start sm:items-start w-full">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleProfileClick(post?.username);
                }}
              >
                <Image
                  src={
                    post?.author?.profile_picture?.thumbnail_url ||
                    post?.author?.profile_picture?.url ||
                    defaultProfile
                  }
                  alt={post?.username}
                  width={40}
                  height={40}
                  className="rounded-full w-10 h-10"
                  loading="lazy"
                />
              </div>

              {/* Content column */}
              <div className="w-full flex-1">
                {/* Header row (wraps on mobile so assoc + time fall to next line together) */}
                <div className="flex flex-wrap items-center w-full ">
                  {/* Row 1: Username, handle, time, type, menu (desktop: all in one row; mobile: first row) */}

                  <div className="flex items-center min-w-0 w-full lg:mb-2 order-1">
                    <HoverableMention user={user} variant="postDisplay">
                      <div
                        className={`flex items-center gap-1 lg:max-w-60 max-w-40 flex-1`}
                      >
                        <span
                          className={
                            `font-bold ml-2 text-base text-gray-900 dark:text-white ` +
                            (showFullHeader
                              ? "lg:max-w-60  truncate text-nowrap"
                              : "truncate  lg:max-w-[60%] ")
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProfileClick(post?.author.name);
                          }}
                        >
                          {displayNameShort}
                        </span>

                        <span
                          className={
                            `text-gray-500 dark:text-gray-400 text-sm ` +
                            (showFullHeader
                              ? "max-w-full"
                              : "truncate ml-1 max-w-[40%] sm:max-w-none")
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProfileClick(post.author.name);
                          }}
                        >
                          @{handleShort}
                        </span>
                      </div>
                    </HoverableMention>
                    {/* <span className="absolute z-50 left-16 top-2 mt-4 hidden group-hover:block bg-green-600 text-white text-sm px-2 py-1 rounded text-wrap">@{post?.username}</span> */}

                    {hasTypeTag && (
                      <span
                        className={`px-2 py-0.5 text-xs rounded-lg whitespace-nowrap text-black order-2 ml-1 ${
                          post.type.toLowerCase() === "problem"
                            ? "bg-gradient-to-tr from-rose-400 to-pink-500 ml-1"
                            : post.type.toLowerCase() === "praise"
                              ? "bg-gradient-to-tr from-[#e0aa3e] to-[#f9f295] ml-1"
                              : "bg-gradient-to-tr from-blue-400 to-indigo-500"
                        }`}
                      >
                        {post.type.charAt(0).toUpperCase() +
                          post.type.slice(1).toLowerCase()}
                      </span>
                    )}
                    {/* Association (visible on all viewports; will wrap under name on mobile) */}
                    <span className="order-3 ml-1">
                      {hasAssociation && (
                        <div className="relative group h-[20px]">
                          <span
                            className="text-sm text-gray-500 ml-1 h-[20px]"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <DropdownMenu
                              modal={false}
                              open={openDropdownId === post.id + "-desktop"}
                              onOpenChange={(isOpen) =>
                                setOpenDropdownId(
                                  isOpen ? post.id + "-desktop" : null
                                )
                              }
                            >
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="secondary"
                                  className={`p-0 rounded-full  h-[20px] ${
                                    post.type.toLowerCase() === "problem"
                                      ? "hover:bg-gradient-to-tr from-rose-400 to-pink-500 hover:text-black"
                                      : "hover:bg-gradient-to-tr from-[#e0aa3e] to-[#f9f295] hover:text-black"
                                  }`}
                                >
                                  <CircleUser className="max-w-fit max-h-fit " />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-fit">
                                {(post.associated_with ?? []).map(
                                  (item, index) => (
                                    <DropdownMenuItem key={index}>
                                      <div
                                        className="flex items-center cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleProfileClick(item.name);
                                        }}
                                      >
                                        <Image
                                          src={item.avatar || defaultProfile}
                                          alt={item.name}
                                          width={24}
                                          height={24}
                                          className="w-6 h-6 rounded-full object-cover"
                                          loading="lazy"
                                        />
                                        <span className="ml-2 max-w-20 truncate">
                                          {item.name}
                                        </span>
                                      </div>
                                    </DropdownMenuItem>
                                  )
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </span>

                          {/* Tooltip - hide when dropdown is open */}
                          {openDropdownId !== post.id + "-desktop" && (
                            <span
                              className="absolute lg:left-8 right-6  top-0 mt-2
                                hidden  group-hover:block
                                dark:bg-white  dark:text-black text-sm 
                                bg-black text-white
                                px-3 py-1 rounded w-60
                                text-wrap z-20 md:min-w-full"
                            >
                              {post.type.toLowerCase() === "praise"
                                ? "People Praised in this Post"
                                : "People associated with this problem"}
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {/* Timestamp: desktop inline, hidden on mobile */}
                    <div className="relative group hidden sm:flex items-center flex-shrink-0 ml-2 sm:ml-3 order-4">
                      <span className="text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap h-[20px] flex items-center">
                        {formatTimestamp(post?.seconds_ago)}
                      </span>

                      <span
                        className="absolute hidden group-hover:block
                            left-0 top-full mt-1
                            bg-gray-800 text-white text-xs
                            px-2 py-1 rounded shadow-lg
                            whitespace-nowrap z-50"
                      >
                        {formatTimestamp(post?.seconds_ago)}
                      </span>
                    </div>

                    <div
                      className="ml-auto flex items-center gap-2 order-5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* AI tag - desktop only */}
                      {hasAiFields && (
                        <div className="hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 dark:from-cyan-500/30 dark:to-emerald-500/30 border border-cyan-400/30 dark:border-cyan-400/50">
                          <Sparkles className="w-3 h-3 text-cyan-600 dark:text-cyan-400" />
                          <span className="text-xs font-semibold text-cyan-700 dark:text-cyan-300">
                            AI
                          </span>
                        </div>
                      )}
                      <PostMenu
                        postId={post?.id}
                        isAuthor={isAuthor}
                        post={post}
                        onPostUpdate={onLoadMore}
                      />
                    </div>
                  </div>

                  {/* Mobile-only timestamp and AI tag on its own row under header */}
                  <div className="w-full sm:hidden -mt-1 order-6 flex items-center gap-2 ml-2">
                    <span className="text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap">
                      {formatTimestamp(post?.seconds_ago)}
                    </span>
                    {/* AI tag - mobile only, after timestamp */}
                    {hasAiFields && (
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 dark:from-cyan-500/30 dark:to-emerald-500/30 border border-cyan-400/30 dark:border-cyan-400/50">
                        <Sparkles className="w-3 h-3 text-cyan-600 dark:text-cyan-400" />
                        <span className="text-xs font-semibold text-cyan-700 dark:text-cyan-300">
                          AI
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Post text content */}

          <div className="flex flex-col w-full">
            <div className="flex flex-col">
              {post?.content && (
                <div
                  className="text-gray-800 dark:text-gray-100 text-base break-words whitespace-pre-wrap p-2 max-w-full min-w-0 border rounded-2xl transition-transform duration-200 ease-in-out transform hover:scale-[1.005] overflow-hidden"
                  style={{ wordBreak: "break-word" }}
                >
                  <p
                    ref={(el) => {
                      contentRefs.current[post.id] = el;
                    }}
                    className={clsx(
                      "transition-all",
                      !expandedPosts[post.id] && "overflow-hidden",
                      !expandedPosts[post.id] && "line-clamp-6 sm:line-clamp-15"
                    )}
                    style={{
                      WebkitBoxOrient: "vertical",
                      display: "-webkit-box",
                    }}
                  >
                    {linkifyReactNode(
                      parsePostText(
                        post?.content,
                        post?.mentioned?.map((m) => ({
                          id: String(m.id),
                          name: m.name,
                          avatar: m.avatar,
                          bio: m.profile_bio,
                        })) || [],
                        (post?.tags || []).map((t) => ({
                          tag_id: t.id,
                          tag_name: t.name,
                        }))
                      )
                    )}
                  </p>
                  {overflowingPosts[post.id] && (
                    <button
                      className="mt-2 text-blue-500 hover:underline text-sm font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedPosts((prev) => ({
                          ...prev,
                          [post.id]: !prev[post.id],
                        }));
                      }}
                    >
                      {expandedPosts[post.id] ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}
              {/* Location if available */}
              {formattedLocation && (
                <button
                  type="button"
                  className="flex items-center text-sm text-gray-600 hover:text-blue-600 dark:text-muted-foreground mb-2 max-w-72 md:max-w-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    // If we have coordinates, use them directly; otherwise fall back to location name
                    if (
                      post?.location_coordinates &&
                      post.location_coordinates.length === 2
                    ) {
                      const [lng, lat] = post.location_coordinates;
                      router.push(
                        `/search?q=${encodeURIComponent(formattedLocation)}&tab=location&lat=${lat}&lng=${lng}`
                      );
                    } else {
                      router.push(
                        `/search?q=${encodeURIComponent(formattedLocation)}&tab=location`
                      );
                    }
                  }}
                  title={`Find posts near ${formattedLocation}`}
                >
                  <MapPin size={14} className="mr-1 flex-shrink-0" />
                  <span className="truncate">{formattedLocation}</span>
                </button>
              )}
            </div>

            <div className="flex-1">
              {/* Media content */}
              {post?.media && post?.media.length > 0 && (
                <div
                  className="mt-2 mb-3 relative group"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Carousel
                    opts={{ watchDrag: watchDrag }}
                    data-carousel-container
                  >
                    <CarouselContent>
                      {post?.media.map((item, idx) => {
                        const errorKey = `${post?.id}-${idx}`;
                        const hasError =
                          mediaErrors[errorKey] ||
                          (item.thumbnail_url &&
                            isMediaFailed(item.thumbnail_url));
                        return (
                          <CarouselItem key={idx}>
                            <div
                              className="relative w-full aspect-square md:aspect-video bg-secondary rounded-lg overflow-hidden"
                              onClick={(e) => {
                                e.stopPropagation();
                                !hasError &&
                                  handleMediaClick(
                                    item,
                                    post?.media || [],
                                    idx
                                  );
                              }}
                            >
                              {hasError ? (
                                <div className="w-full h-full flex items-center justify-center bg-muted">
                                  <div className="text-center">
                                    <ImageIcon
                                      size={48}
                                      className="mx-auto mb-2 text-muted-foreground"
                                    />
                                    <p className="text-sm text-muted-foreground">
                                      Media unavailable
                                    </p>
                                  </div>
                                </div>
                              ) : item.type === "video" ? (
                                item.processing_state === "failed" ? (
                                  <div
                                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                                    role="status"
                                  >
                                    <CircleX
                                      className="text-red-600"
                                      size={36}
                                    />
                                    <div className="ml-1">
                                      <p>Failed to load the video</p>
                                    </div>
                                  </div>
                                ) : item.processing_state === "pending" ? (
                                  <div
                                    // This is the full-screen overlay
                                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                                    role="status"
                                  >
                                    <div className="flex items-center  rounded-lg bg-white p-5 shadow-lg">
                                      <Spinner className="text-black mr-1" />

                                      <div>
                                        <p className="text-lg font-medium text-gray-800">
                                          Processing...
                                        </p>
                                        <p className="text-sm text-gray-500">
                                          This may take a few minutes.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="absolute inset-0 w-full h-full no-drag"
                                    onClick={(e) => e.stopPropagation()}

                                    // stops drag events from bubbling
                                  >
                                    <VideoPlayer
                                      videoUrl={item.url}
                                      thumbnailUrl={item.thumbnail_url}
                                    />
                                  </div>
                                )
                              ) : (
                                <Image
                                  src={item.url}
                                  alt="Post media"
                                  fill
                                  loading="lazy"
                                  className="object-contain"
                                  sizes="(max-width: 768px) 100vw, 50vw"
                                  onError={() => {
                                    if (item.thumbnail_url) {
                                      handleMediaError(
                                        Number(post?.id),
                                        idx,
                                        item.thumbnail_url
                                      );
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                    {post?.media.length > 1 && (
                      <>
                        <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white disabled:hidden" />
                        <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white disabled:hidden" />
                      </>
                    )}
                  </Carousel>
                </div>
              )}
              {/* Link previews: automated post uses post content; otherwise one preview per link */}
              <div className="space-y-2 max-w-full mb-3">
                {post?.automated_post ? (
                  <AutomatedPostLinkPreview post={post} />
                ) : (
                  post?.links?.map((link, idx) =>
                    extractYouTubeVideoId(link) ? null : (
                      <LinkPreview key={idx} url={link} />
                    )
                  )
                )}
              </div>
              {/* YouTube embed*/}
              {post.links && post.links.length > 0 && (
                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                  {post.links.map((link, index) => {
                    const videoId = extractYouTubeVideoId(link);
                    if (videoId) {
                      return <VideoPlayer videoUrl={link} key={index} />;
                    }
                    return null;
                  })}
                </div>
              )}
              {/* Actions */}
              <div
                className="flex items-center justify-between mt-2"
                onClick={(e) => e.stopPropagation()}
              >
                {/* 1. Like Button */}
                <button
                  className="flex items-center justify-center gap-1 text-gray-400 hover:text-blue-500 min-w-[40px] min-h-[40px] rounded-full"
                  onClick={() => handleLikeClick(post)}
                >
                  <ReactionPopup
                    id={post.id}
                    likes={post?.total_reactions}
                    isLikedByMe={Boolean(post?.reaction_by_me)}
                    onCountClick={openReactionsPanel}
                    POST_ACTION_BUTTON_SIZE={POST_ACTION_BUTTON_SIZE}
                  />
                </button>

                {/* 2. Comment Button */}
                <button
                  onClick={() => handleCommentClick(post.id)}
                  className="flex items-center justify-center gap-1 text-gray-400 hover:text-red-500 min-w-[40px] min-h-[40px] rounded-full"
                >
                  <MessageCircle size={POST_ACTION_BUTTON_SIZE} />
                  <span className="text-lg font-bold ">
                    {countReduce(post?.total_comments)}
                  </span>
                </button>

                {/* 3. Impressions Div with Tooltip */}
                <div
                  title="Impressions"
                  className="flex items-center justify-center gap-1 text-gray-400 hover:text-green-500 min-w-[40px] min-h-[40px]"
                >
                  <Eye size={POST_ACTION_BUTTON_SIZE} />
                  <span className="text-lg font-bold ">
                    {countReduce(post.impressions)}
                  </span>
                </div>

                {/* 4. Share Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(post);
                  }}
                  className="flex items-center justify-center gap-1 text-gray-400 hover:text-blue-500 min-w-[40px] min-h-[40px] rounded-full"
                >
                  <Share2 size={POST_ACTION_BUTTON_SIZE} />
                </button>
              </div>

              {/* AI Analysis toggle */}
              {hasAiFields && (
                <div className="mt-2">
                  <button
                    className={`group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-2xl text-sm font-semibold
                      bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50
                      dark:bg-white/5 dark:text-cyan-200 dark:border-white/10 dark:backdrop-blur
                      dark:hover:bg-white/10 transition-colors`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAiOpenMap((prev) => ({
                        ...prev,
                        [String(post.id)]: !prev[String(post.id)],
                      }));
                    }}
                  >
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white">
                      <Sparkles className="w-3.5 h-3.5" />
                    </span>
                    <span>AI Analysis</span>
                    <span
                      className={`transition-transform duration-200 ${
                        aiOpenMap[String(post.id)] ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <ChevronDown className="w-4 h-4 text-gray-500 dark:text-cyan-300" />
                    </span>
                  </button>
                </div>
              )}

              {/* AI Analysis Panel */}
              {hasAiFields && aiOpenMap[String(post.id)] && (
                <div onClick={(e) => e.stopPropagation()} className="mt-3">
                  <AiAnalysis ai_analysis={post?.ai_analysis} />
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const postsToDisplay = context === "profile" ? userPosts : posts;

  return (
    <div className="divide-y divide-border">
      {postsToDisplay?.length > 0 ? (
        postsToDisplay.map((post, idx, arr) => (
          <React.Fragment key={post.id}>
            {renderPost(post, idx, arr)}
            {(idx + 1) % 6 === 0 && (
              <AdComponent adType="feed" id={`${post.id}-ad-${idx}`} />
            )}
          </React.Fragment>
        ))
      ) : (
        <div className="text-center">
          <p className="text-lg text-muted-foreground">No posts to show</p>
        </div>
      )}

      {onLoadMore && loading && (postsToDisplay.length === 0 || hasMore) && (
        <div className="p-4 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Media Popup Dialog */}
      {selectedMedia && (
        <Dialog
          open={!!selectedMedia}
          onOpenChange={() => setSelectedMedia(null)}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-transparent border-none">
            {/* <DialogHeader className="absolute top-4 right-4 z-50">
              <button
                className="p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                onClick={() => setSelectedMedia(null)}
                aria-label="Close media popup"
              >
                <X className="w-6 h-6" />
              </button>
            </DialogHeader> */}
            {/* 
            <DialogTitle className="p-4 bg-background/80 backdrop-blur-sm">
              Preview
            </DialogTitle> */}

            <Carousel
              data-carousel-container
              setApi={(api) => {
                if (api) {
                  api.scrollTo(selectedMedia.currentIndex);
                  api.on(
                    "select",
                    (event: { selectedScrollSnap: () => number }) => {
                      handleMediaChange(event.selectedScrollSnap());
                    }
                  );
                }
              }}
            >
              <CarouselContent>
                {selectedMedia.media.map((item, index) => (
                  <CarouselItem key={index}>
                    <div className="relative w-full h-[80vh] flex items-center justify-center bg-black/90">
                      {item.type === "video" ? (
                        item.processing_state === "failed" ? (
                          <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                            role="status"
                          >
                            <CircleX className="text-red-600" size={36} />
                            <div className="ml-1">
                              <p>Failed to load the video</p>
                            </div>
                          </div>
                        ) : item.processing_state === "pending" ? (
                          <div
                            // This is the full-screen overlay
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                            role="status"
                          >
                            <div className="flex items-center  rounded-lg bg-white p-5 shadow-lg">
                              <Spinner className="text-black mr-1" />

                              <div>
                                <p className="text-lg font-medium text-gray-800">
                                  Processing...
                                </p>
                                <p className="text-sm text-gray-500">
                                  This may take a few minutes.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="absolute inset-0 w-full h-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <VideoPlayer videoUrl={item.url} />
                          </div>
                        )
                      ) : (
                        <Image
                          src={item.url}
                          alt="Fullscreen media"
                          fill
                          className="object-contain"
                          loading="lazy"
                        />
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {selectedMedia.media.length > 1 && (
                <>
                  <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white hover:bg-black/70" />
                  <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white hover:bg-black/70" />
                </>
              )}
            </Carousel>
          </DialogContent>
        </Dialog>
      )}

      {/* Render the right-anchored dropdown for association info (mobile only): */}
      {showAssociationDropdown &&
        selectedAssociationPost &&
        dropdownPosition && (
          <div
            ref={associationDropdownRef}
            className="absolute z-50 bg-white dark:bg-gray-900 shadow-lg rounded-xl p-2 border"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: "200px",
              maxWidth: "240px",
              maxHeight: "320px",
              overflowY: "auto",
            }}
          >
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
              onClick={() => {
                setShowAssociationDropdown(false);
                setSelectedAssociationPost(null);
              }}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="mb-2 font-semibold text-center text-gray-700 dark:text-gray-200">
              Associated with
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {selectedAssociationPost.associated_with &&
                selectedAssociationPost.associated_with.map(
                  (item: any, index: number) => (
                    <div
                      key={index}
                      className="flex items-center py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-2 rounded"
                      onClick={() => {
                        setShowAssociationDropdown(false);
                        setSelectedAssociationPost(null);
                        handleProfileClick(item.name);
                      }}
                    >
                      <Image
                        src={item.avatar || defaultProfile}
                        alt={item.name}
                        width={28}
                        height={28}
                        className="w-7 h-7 rounded-full object-cover"
                        loading="lazy"
                      />
                      <span className="ml-3 text-gray-900 dark:text-gray-100">
                        {item.name}
                      </span>
                    </div>
                  )
                )}
            </div>
          </div>
        )}

      {/* Share Panel */}
      <SharePanel
        isOpen={isSharePanelOpen}
        onClose={closeSharePanel}
        post={sharePost}
      />

      {/* Reactions Panel */}
      <ReactionsPanel
        key={reactionsPostId || "post-list-reactions-panel"}
        isOpen={isReactionsOpen}
        onClose={closeReactionsPanel}
        postId={reactionsPostId}
      />
    </div>
  );
};

export default PostDisplay;
